import { useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth, useUser } from "@/hooks/useAuthV2";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { User, Mail, Building, Shield, Calendar, ArrowLeft, Camera, Edit } from "lucide-react";
import { organizationsApi, usersApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import type { UserUpdate } from "@/types/api-v2";

const ROLE_TITLES: Record<number, string> = {
  1: "Администратор",
  2: "Редактор",
  3: "Аналитик",
  4: "Наблюдатель",
};

export default function ProfilePage() {
  const { user, isLoading: authLoading, hasPermission } = useAuth();
  const { refetch: refetchUser } = useUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
  });

  const { data: organization } = useQuery({
    queryKey: ["profile-organization", user?.organization_id],
    queryFn: () => organizationsApi.get(user!.organization_id!),
    enabled: !!user?.organization_id,
  });

  const roleName = useMemo(() => {
    if (!user?.role_id) return "Пользователь";
    return ROLE_TITLES[user.role_id] || "Пользователь";
  }, [user?.role_id]);

  // Инициализация формы при открытии диалога
  const handleOpenEditDialog = () => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        username: user.username || "",
      });
    }
    setIsEditDialogOpen(true);
  };

  // Мутация для обновления профиля
  const updateProfileMutation = useMutation({
    mutationFn: async (data: UserUpdate) => {
      if (!user?.id) throw new Error("Пользователь не найден");
      return await usersApi.update(user.id, data);
    },
    onSuccess: async (updatedUser) => {
      toast({
        title: "Профиль обновлен",
        description: "Ваши данные успешно сохранены",
      });
      setIsEditDialogOpen(false);
      // Обновляем данные пользователя в кэше
      queryClient.setQueryData(["auth", "me"], updatedUser);
      await refetchUser();
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка обновления",
        description: error.message || "Не удалось обновить профиль",
        variant: "destructive",
      });
    },
  });

  // Обработка загрузки фото
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Валидация типа файла
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Неверный тип файла",
        description: "Пожалуйста, выберите изображение",
        variant: "destructive",
      });
      return;
    }

    // Валидация размера (макс 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Файл слишком большой",
        description: "Максимальный размер файла: 5MB",
        variant: "destructive",
      });
      return;
    }

    // Конвертация в base64 для простоты (в продакшене лучше использовать загрузку файла)
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result as string;
      try {
        await updateProfileMutation.mutateAsync({
          profile_image_url: base64String,
        });
      } catch (error) {
        // Ошибка уже обработана в onError
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(formData);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Загрузка профиля...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6">
        {/* Навигация */}
        <div className="mb-6 flex flex-col gap-2">
          <Link to="/">
            <Button variant="ghost" className="w-fit">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Вернуться на главную
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-foreground">Профиль пользователя</h1>
          <p className="text-muted-foreground mt-1">Управление личной информацией и настройками</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Основная информация */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Личная информация
                </CardTitle>
                <CardDescription>
                  Основные данные вашего профиля
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Имя</Label>
                      <p className="text-sm text-foreground mt-1">
                        {user?.first_name || "Не указано"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Фамилия</Label>
                      <p className="text-sm text-foreground mt-1">
                        {user?.last_name || "Не указано"}
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                    <p className="text-sm text-foreground mt-1 flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {user?.email || "Не указан"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Имя пользователя</Label>
                    <p className="text-sm text-foreground mt-1">
                      {user?.username || "Не указано"}
                    </p>
                  </div>
                  <Button 
                    onClick={handleOpenEditDialog}
                    className="w-full mt-4"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Редактировать профиль
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Боковая панель */}
          <div className="space-y-6">
            {/* Аватар и основная информация */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="relative inline-block mb-4">
                          <Avatar className="h-24 w-24">
                            <AvatarImage 
                              src={user?.profile_image_url ? String(user.profile_image_url) : undefined} 
                              alt="Profile" 
                            />
                            <AvatarFallback className="text-2xl">
                              {user?.first_name?.[0] || user?.email?.[0] || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute bottom-0 right-0 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-2 shadow-lg transition-colors border-2 border-background"
                            type="button"
                            aria-label="Добавить фото"
                          >
                            <Camera className="h-4 w-4" />
                          </button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Добавить фото</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <h3 className="text-lg font-semibold">
                    {user?.first_name && user?.last_name 
                      ? `${user.first_name} ${user.last_name}`
                      : user?.email?.split('@')[0] || "Пользователь"
                    }
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Информация о роли и организации */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Система доступа</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Роль</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    <Badge variant={hasPermission("admin.access") ? "destructive" : "secondary"}>
                      {roleName}
                    </Badge>
                  </div>
                </div>
                
                {organization && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Орган управления</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      <span className="text-sm text-foreground">
                        {(organization as any)?.name}
                      </span>
                    </div>
                  </div>
                )}

                <Separator />
                
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Дата регистрации</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm text-foreground">
                      {user?.created_at 
                        ? new Date(user.created_at).toLocaleDateString('ru-RU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : "Не указана"
                      }
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Диалог редактирования профиля */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактировать профиль</DialogTitle>
            <DialogDescription>
              Измените данные вашего профиля. Нажмите сохранить, когда закончите.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Имя</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Введите имя"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Фамилия</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Введите фамилию"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Введите email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="username">Имя пользователя</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Введите имя пользователя"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                disabled={updateProfileMutation.isPending}
              >
                Отмена
              </Button>
              <Button type="submit" disabled={updateProfileMutation.isPending}>
                {updateProfileMutation.isPending ? "Сохранение..." : "Сохранить"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}